import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-location';
import {
  NoShowRateByType,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { Chart } from '@frontend/charts';
import { DashboardWidget, DashboardWidgetFC, useDashboardWidget } from '@frontend/grid-dashboard';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { ContentLoader, Text } from '@frontend/design-system';
import { URLs } from '../../../constants';
import { featureFlags } from '../../../feature-flags';
import { useFetchARROIOOverview } from '../appointment-reminders/hooks';
import { customROITimePeriods } from '../constants';
import { useFetchNSRROITimeline } from '../no-show-rate/hooks';
import { CustomTimeHeader } from './custom-time-header';

/**
 * @dashboard-widget
 *
 * id: no-show-rate
 * title: No-Show Rate
 * description: Track no-show performance for your location with historical trends
 * icon: user
 */

export const NoShowRateWidget: DashboardWidgetFC = () => {
  const timePeriod = customROITimePeriods.thisQuarter;
  const { isEditMode } = useDashboardWidget();
  const { selectedLocationIds } = useAppScopeStore();
  const navigate = useNavigate();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.noShowRateRoiDemoData);

  const filters = {
    startDate: timePeriod.value.startDate,
    endDate: timePeriod.value.endDate,
    locationIds: selectedLocationIds,
  };

  const { chartData, isLoading: isLoadingTimeline } = useFetchNSRROITimeline({
    additionalRequestPayload: {
      roiType: RoiType.WEAVE,
      noShowRateType: NoShowRateByType.WEAVE_AVERAGE,
    },
    filters,
  });

  const { aggregated, isLoading: isLoadingNSRRate } = useFetchARROIOOverview({
    fetchNoShowRate: false,
    filters,
    forceDemoData: isDemoAccount,
    roiType: RoiType.WEAVE,
  });

  const trend = useMemo(() => {
    return (chartData?.groups ?? []).map(({ values }) => values.practice) || [];
  }, [chartData]);

  return (
    <DashboardWidget
      onClick={
        isEditMode
          ? undefined
          : () => {
              navigate({
                to: URLs.ROI_NO_SHOW_RATE,
              });
            }
      }
    >
      <DashboardWidget.Header>
        <CustomTimeHeader label={timePeriod.label} />
      </DashboardWidget.Header>
      <DashboardWidget.Content css={styles.widgetContent}>
        <Text style={{ fontSize: theme.font.size.h2 }} weight='bold'>
          {formatters.appendPercent(
            isDemoAccount ? Math.abs(aggregated.noShowRatePercentage) : aggregated.noShowRatePercentage
          )}
        </Text>
        <div css={styles.chartContainer}>
          <Chart.TinyAreaChart data={trend} color={theme.colors.critical30} />
        </div>
        <ContentLoader show={isLoadingTimeline || isLoadingNSRRate} size='small' />
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

NoShowRateWidget.config = {
  size: 'small-narrow',
  feature: 'analytics',
};

const styles = {
  widgetContent: css`
    position: relative;
    overflow: hidden;
  `,

  chartContainer: css`
    height: ${theme.spacing(5)};
    width: 100%;
  `,
};
